import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  addDays,
  compactTime,
  countedBlocks,
  formatLongDate,
  formatShortDate,
  formatTimeRange,
  monthName,
  snapToQuarter,
  startOfWeek,
  suggestSlots,
  toDate,
  CALENDAR_DAY_START_HOUR,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
  type Task,
} from '@app/shared'

import {
  useCalendarRange,
  useClock,
  useCreateCalendarBlock,
  useDeleteCalendarBlock,
  useSyncStatus,
  useTasks,
  useUpdateCalendarBlock,
} from '../lib/data'
import { DataModeBadge, HeaderBand } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
// The same relative-time phrasing the Connections and School screens use, imported rather than
// re-derived: "6 min ago" has to mean the same thing on every screen that says it.
import { agoPhrase } from '../components/connections/labels'
import { ChevronLeft, ChevronRight, Plus } from '../components/calendar/icons'
import { Composer, type ComposerDraft } from '../components/calendar/Composer'
import { DayView, type DragProposal } from '../components/calendar/DayView'
import { MonthView } from '../components/calendar/MonthView'
import { WeekView } from '../components/calendar/WeekView'
import { CalendarPeek, type PeekContent } from '../components/calendar/Peek'
import { type PeekAnchor } from '../lib/popover'
import { MiniMonth, ReadOnlyNote, SourcesList, type SourceFilterKey } from '../components/calendar/sidebar'
import {
  FocusCard,
  RailFootnote,
  UnscheduledCard,
  WhereTodayGoesCard,
} from '../components/calendar/rail'
import { monthMatrix, nextDeadline, unscheduledTasks, MONTH_CELLS } from '../components/calendar/derive'
import { CAL_BLOCK_ATTR, useBlockDrag, type BlockDrop } from '../components/calendar/useBlockDrag'
import { blockLands } from '../lib/motion'

// ============================================================================
// CALENDAR — docs/design/calendar-brief.md, with LifeOS Calendar.dc.html (11a week, 11b day) as
// the visual reference. Where the brief and the mockup disagree, the brief wins; it says so.
//
// WHAT THIS SCREEN IS FOR: seeing the week you actually have before deciding what to put in it.
// Google Calendar's grid, LifeOS's honesty — which comes down to four rules that live in the
// model rather than in this file (see @app/shared/calendar.ts and the Worker route):
//
//   1. CANVAS IS READ-ONLY, and not in the disabled-button sense. A Canvas block renders with no
//      handlers at all, because there is no row behind it that any endpoint would accept a write
//      for. `editable` is a field on the wire, not a class name here.
//   2. NOTHING INVENTS A SLOT. Suggestions come only from tasks that carry an owner-supplied
//      estimate, they are labelled SUGGESTED FROM TASKS, they carry Keep / Dismiss, and until
//      Keep is pressed they exist ONLY in this component's memory — nothing counts them, nothing
//      stores them, nothing can remind on them.
//   3. TIME SUMMARIES COUNT PLACED BLOCKS. The day summary states its denominator and subtracts
//      the remainder rather than estimating it.
//   4. THE SOURCES LIST TELLS THE TRUTH. Every row is a real filter — INCLUDING Google, since
//      the iCal connector landed. The row is a switch only when `sources` says Google has
//      actually synced; before that it is still the dimmed `off` statement the brief drew, and
//      outside the window the mirror covers the row says THAT rather than letting an empty grid
//      read as a free week.
//
// MONTH IS NO LONGER A STUB. The brief's Scope line reads "Ship Day + Week. Month is a stub …
// UNLESS TOLD OTHERWISE", and the owner told otherwise (2026-08-28). The month grid lives in
// ./components/calendar/MonthView.tsx; it is A WIDER RANGE OF THE SAME READ — 42 days of the
// one `useCalendarRange` query, no new endpoint — and it carries the four rules above unchanged.
// It has no drag in v1: dropping a block on a day would mean inventing a time of day for it.
// ============================================================================

type ViewMode = 'day' | 'week' | 'month'

/** What a drop with no estimate behind it holds. Stated in the tooltip, never inferred as fact. */
const DEFAULT_DROP_MINUTES = 30

/**
 * ONE OVERLAY AT A TIME, AS A TYPE.
 *
 * The peek and the composer used to be two independent `useState`s, and two independent states is
 * two states that can both be true: opening the editor over an open peek left the peek behind it,
 * and the owner reported exactly that. Exclusivity spelled as a union cannot be forgotten by the
 * next handler that opens one of them, because there is no second slot to leave a stale panel in.
 *
 * `anchor: null` on a composer is a real answer and the only one that is not a rect: it is what
 * the header's `+ New` passes, and it is what selects the centred placement.
 */
type Overlay =
  | { kind: 'peek'; anchor: PeekAnchor; content: PeekContent }
  | { kind: 'composer'; anchor: PeekAnchor | null; draft: ComposerDraft }

export function Calendar() {
  const { data: clock } = useClock()
  const { data: sync, error: syncError } = useSyncStatus()
  const { data: tasks } = useTasks({ done: false })

  // THE VIEW AND THE DAY LIVE IN THE URL, not only in state. Three reasons, and the third is the
  // one that decided it: a week is a thing you send yourself ("/calendar?view=day&date=…"), the
  // back button should undo a day you opened by clicking a column header, and a screenshot
  // harness has no way to press a toggle. Absent parameters mean the defaults — week, today.
  const [params, setParams] = useSearchParams()
  const view: ViewMode =
    params.get('view') === 'day' ? 'day' : params.get('view') === 'month' ? 'month' : 'week'
  const urlDate = params.get('date')
  const [stateAnchor, setStateAnchor] = useState<ISODate | null>(null)
  const anchor = (/^\d{4}-\d{2}-\d{2}$/.test(urlDate ?? '') ? (urlDate as ISODate) : stateAnchor)

  const setView = (next: ViewMode) => {
    const merged = new URLSearchParams(params)
    merged.set('view', next)
    setParams(merged, { replace: true })
  }
  const setAnchor = (next: ISODate | null) => {
    setStateAnchor(next)
    const merged = new URLSearchParams(params)
    if (next) merged.set('date', next)
    else merged.delete('date')
    setParams(merged, { replace: true })
  }
  const [monthAnchor, setMonthAnchor] = useState<ISODate | null>(null)
  const [hidden, setHidden] = useState<ReadonlySet<SourceFilterKey>>(new Set())
  const [dragging, setDragging] = useState<Task | null>(null)
  const [proposal, setProposal] = useState<DragProposal | null>(null)
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set())
  /** The one open panel, or none. See `Overlay` — the exclusivity is the type, not a convention. */
  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const draft = overlay?.kind === 'composer' ? overlay.draft : null
  const openComposer = (draft: ComposerDraft, anchor: PeekAnchor | null) =>
    setOverlay({ kind: 'composer', draft, anchor })
  const closeOverlay = () => setOverlay(null)

  const create = useCreateCalendarBlock()
  const update = useUpdateCalendarBlock()
  const remove = useDeleteCalendarBlock()

  const today = clock?.date
  // The anchor defaults to today and only then becomes state. Seeding it from `useState`'s
  // initialiser would freeze it at whatever the clock said on first render — including `null`.
  const focus = anchor ?? today
  const weekStart = focus ? startOfWeek(focus) : undefined
  // An overlay's anchor is a rect captured at click time, not a live reference to the block — it
  // goes stale the moment the day or week changes underneath it, so a view change closes whatever
  // is open rather than leaving a panel pointed at nothing.
  useEffect(() => setOverlay(null), [view, focus])
  // ONE READ, THREE SPANS. A month is not a different question — it is the same one asked over
  // 42 days, which is inside the Worker's 62-day ceiling with room to spare. The month's span is
  // the MATRIX's, not the calendar month's: the leading and trailing cells are drawn, so their
  // events have to be read, or the first row of a month would look empty for no reason.
  const range = useMemo(() => {
    if (view === 'day') return focus ? { from: focus, to: focus } : undefined
    if (view === 'month') {
      if (!focus) return undefined
      const cells = monthMatrix(focus)
      return { from: cells[0]!, to: cells[MONTH_CELLS - 1]! }
    }
    return weekStart ? { from: weekStart, to: addDays(weekStart, 6) } : undefined
  }, [view, focus, weekStart])
  const { data: calendar, isPending, error } = useCalendarRange(range)

  const nowMinutes = clock?.minutesIntoDay ?? null

  // ── Filtering. The sources list is a real control, so this is what it controls. ────────────
  const visibleBlocks = useMemo(() => {
    const blocks = calendar?.blocks ?? []
    return blocks.filter((b) => {
      if (b.source === 'canvas') return !hidden.has('canvas')
      if (b.source === 'google') return !hidden.has('google')
      if (b.source === 'work') return !hidden.has('work')
      if (b.source === 'personal') return !hidden.has('personal')
      // A block held for a task IS a learning block as far as the palette and this list are
      // concerned — the brief's table calls that row "Learning / suggested".
      return !hidden.has('learning')
    })
  }, [calendar, hidden])

  const visibleDue: CalendarDueItem[] = hidden.has('canvas') ? [] : (calendar?.dueItems ?? [])
  const visibleAllDay: CalendarAllDayEvent[] = hidden.has('google')
    ? []
    : (calendar?.allDayEvents ?? [])

  // ── What the Google row in the sources list is allowed to claim ───────────────────────────
  //
  // Both facts come off the wire and neither is inferred here. `gcalRow` is the connector's own
  // `sources` row, so a switch appears only once a real sync has happened; `googleWindow` is the
  // span the mirror actually covers, so the week on screen can be checked against it instead of
  // an empty grid being left to imply a free week.
  const gcalRow = syncError || !sync ? undefined : sync.find((r) => r.source === 'gcal')
  const googleSyncedAt = gcalRow?.status === 'ok' ? gcalRow.lastSyncAt : null
  const googleWindow = calendar?.googleWindow ?? null
  const outsideGoogleWindow = Boolean(
    googleWindow && range && (range.from < googleWindow.from || range.to > googleWindow.to),
  )

  // ── Suggestions. Ephemeral by construction — see rule 2 in the header. ────────────────────
  const dayBlocks = useMemo(
    () => (focus ? visibleBlocks.filter((b) => b.date === focus) : []),
    [visibleBlocks, focus],
  )

  const suggestions: CalendarBlock[] = useMemo(() => {
    if (view !== 'day' || !focus || !tasks) return []
    const notBefore =
      focus === today && nowMinutes !== null ? nowMinutes : CALENDAR_DAY_START_HOUR * 60
    // ONE PROPOSAL AT A TIME. The engine can find more, and the day view deliberately asks for
    // one: a column of unaccepted suggestions reads as a plan, and a plan is exactly what an
    // unaccepted suggestion is not.
    return suggestSlots(tasks, dayBlocks, notBefore, 1)
      .map((slot) => ({
        id: `suggestion-${slot.task.id}`,
        title: `${slot.task.title} — work block`,
        source: 'task' as const,
        state: 'suggested' as const,
        date: focus,
        startsAt: localTimestamp(focus, slot.startMinutes),
        endsAt: localTimestamp(focus, slot.endMinutes),
        startMinutes: slot.startMinutes,
        endMinutes: slot.endMinutes,
        location: null,
        status: null,
        taskId: slot.task.id,
        editable: true,
        courseCode: null,
        courseSlug: null,
        // A slot suggestion is the CALENDAR's own arithmetic over a task with an estimate — it
        // has no row and no chat proposal behind it. FROM CHAT is a different provenance, and
        // this is where the two stay apart.
        proposalId: null,
      }))
      .filter((s) => !dismissed.has(s.id))
  }, [view, focus, tasks, dayBlocks, dismissed, today, nowMinutes])

  // COUNTS NEVER INCLUDE A SUGGESTION. `countedBlocks` is the same filter the day summary uses.
  // All-day Google events DO count: they are on the calendar, they just are not on the grid.
  const eventCount = countedBlocks(visibleBlocks).length + visibleDue.length + visibleAllDay.length

  // ── Writes ────────────────────────────────────────────────────────────────
  function save(next: ComposerDraft) {
    const payload = {
      title: next.title,
      source: next.source,
      startsAt: localTimestamp(next.date, next.startMinutes),
      endsAt: localTimestamp(next.date, next.endMinutes),
      location: next.location,
      taskId: next.taskId,
      // REPLACED, never merged — so the picker's empty list is how the last reminder is removed.
      // Only the composer ever sends this key: a drag that moves a block PATCHes without it, and
      // therefore cannot silently drop what was set here.
      reminders: next.reminders,
    }
    const done = () => setOverlay(null)
    if (next.id) update.mutate({ id: next.id, patch: payload }, { onSuccess: done })
    else create.mutate(payload, { onSuccess: done })
  }

  function keepSuggestion(block: CalendarBlock) {
    create.mutate({
      title: block.title,
      source: 'task',
      startsAt: block.startsAt,
      endsAt: block.endsAt,
      location: null,
      taskId: block.taskId,
      // The suggestion becomes a real, accepted block — which is the moment everything that
      // counts blocks starts counting it, and not one render earlier.
      state: 'accepted',
    })
    setDismissed((was) => new Set(was).add(block.id))
  }

  /**
   * Run 13d's landing on the block that was just written, once React has actually painted it.
   * Two frames rather than one: the first is the mutation's own commit, the second is the query
   * cache's — animating an element that is about to be replaced is an animation nobody sees.
   */
  function landBlock(id: string) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        blockLands(document.querySelector<HTMLElement>(`[${CAL_BLOCK_ATTR}="${id}"]`))
      })
    })
  }

  function handleDrop(minutes: number, event: React.DragEvent<HTMLDivElement>) {
    if (!dragging || !focus) return
    const start = snapToQuarter(minutes)
    const length = dragging.estimateMin ?? DEFAULT_DROP_MINUTES
    if (event.type === 'drop') {
      create.mutate(
        {
          title: dragging.title,
          source: 'task',
          startsAt: localTimestamp(focus, start),
          endsAt: localTimestamp(focus, start + length),
          location: null,
          taskId: dragging.id,
        },
        // 13d's last beat: THE BLOCK GROWS FROM ITS TOP EDGE ONLY. Not decoration — top-origin
        // growth is what stops the new block visually covering the event above it while it
        // arrives, which would be the grid showing a collision that is not there.
        //
        // Found by its own `data-cal-block` stamp rather than threaded down through DayView →
        // GridColumn → BlockChip as a "you just landed" prop: the grid already publishes that
        // attribute for the drag controller, and four components carrying a flag for one
        // animation is four components that can drop it.
        { onSuccess: (created) => landBlock(created.id) },
      )
      setDragging(null)
      setProposal(null)
      return
    }
    setProposal({ label: dragging.title, startMinutes: start, endMinutes: start + length })
  }

  /**
   * A block that was moved or resized, saved.
   *
   * ONE PATCH, TWO EDGES, ALWAYS BOTH. A move rewrites `startsAt` and `endsAt` together even
   * though only one of them "changed" conceptually, because the day can change too and the two
   * timestamps carry it — and because the Worker checks a one-sided patch against the edge it
   * already stored, which is a round trip this saves. The write is OPTIMISTIC (see
   * useUpdateCalendarBlock): the grid redraws on release and rolls back if the server refuses.
   */
  function saveDrop(drop: BlockDrop) {
    update.mutate({
      id: drop.block.id,
      patch: {
        startsAt: localTimestamp(drop.date, drop.startMinutes),
        endsAt: localTimestamp(drop.date, drop.endMinutes),
      },
    })
  }

  const blockDrag = useBlockDrag(saveDrop)

  /**
   * Click → inline edit, unless the click is the tail of a drag that just happened, or the
   * detail peek — never both. A Canvas class and a Google event are read-only (brief rule 1),
   * so their click opens the PEEK, never the composer; there is no row behind either for the
   * composer's Save to write.
   */
  const openBlock = (block: CalendarBlock, anchor: PeekAnchor) => {
    if (blockDrag.consumeClick()) return
    if (block.state === 'suggested') return
    if (!block.editable) {
      setOverlay({ kind: 'peek', anchor, content: peekFromBlock(block, googleSyncedAt, today, nowMinutes) })
      return
    }
    // ANCHORED TO THE BLOCK. The editor opens beside the thing it is about to edit, which is also
    // the thing the owner is already looking at.
    openComposer(draftOf(block), anchor)
  }

  /** A due pill opens the peek too — Canvas's own deadline, with a link to the course. */
  const openDue = (item: CalendarDueItem, anchor: PeekAnchor) => {
    setOverlay({ kind: 'peek', anchor, content: peekFromDue(item) })
  }

  /** A whole-day Google pill, same peek — the event has no course to link to. */
  const openAllDay = (event: CalendarAllDayEvent, anchor: PeekAnchor) => {
    setOverlay({
      kind: 'peek',
      anchor,
      content: peekFromAllDay(event, googleSyncedAt, today, nowMinutes),
    })
  }

  // ── Chrome ────────────────────────────────────────────────────────────────
  const canvasRow = syncError || !sync ? undefined : sync.find((r) => r.source === 'canvas')
  const meta = [
    focus && weekStart
      ? view === 'day'
        ? formatLongDate(focus)
        : view === 'month'
          ? // The month grid's own label — the month it is showing, not the week inside it. The
            // mini-month in the rail is anchored to the same `focus`, so the two agree by
            // construction rather than by two states that have to be kept in step.
            `${monthName(focus)} ${focus.slice(0, 4)}`
          : rangeLabel(weekStart)
      : undefined,
    calendar ? `${eventCount} ${eventCount === 1 ? 'event' : 'events'}` : undefined,
    canvasRow?.status === 'ok' && canvasRow.lastSyncAt && today && nowMinutes !== null
      ? `Canvas synced ${agoPhrase(canvasRow.lastSyncAt, today, nowMinutes)}`
      : undefined,
  ].filter(Boolean) as string[]

  const step = (delta: number) => {
    if (!focus) return
    if (view === 'month') {
      setAnchor(shiftMonth(focus, delta))
      // The rail follows the grid in month view (see the MiniMonth below), so a month it had
      // been stepped to on its own must not survive a step of the grid.
      setMonthAnchor(null)
      return
    }
    setAnchor(addDays(focus, view === 'day' ? delta : 7 * delta))
  }

  /** 'Previous day' / 'Previous week' / 'Previous month' — the control says what it does. */
  const stepUnit = view === 'day' ? 'day' : view === 'month' ? 'month' : 'week'

  return (
    <>
      <HeaderBand band="calendar">
        <div className="flex w-full items-center gap-[14px]">
          <span className="flex-none text-screen font-semibold">Calendar</span>
          <span
            className="flex items-center gap-[9px] whitespace-nowrap text-[11.5px] font-normal leading-none text-muted"
            title="Events counts the blocks on the grid plus the deadlines in the due row, for the range shown. A suggestion nobody kept is not counted, and neither is a source you have switched off."
          >
            {meta.map((part, i) => (
              <span key={part} className="flex items-center gap-[9px]">
                {i > 0 && <span className="size-[3px] rounded-full bg-dash" aria-hidden />}
                {part}
              </span>
            ))}
          </span>

          <div className="ml-auto flex flex-none items-center gap-[7px]">
            <div className="flex items-center overflow-hidden rounded-pill border border-line bg-surface">
              <button
                type="button"
                aria-label={`Previous ${stepUnit}`}
                onClick={() => step(-1)}
                style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
                className="mo-ink mo-press border-r border-line-4 px-[10px] py-[6px] text-muted"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                onClick={() => {
                  setAnchor(today ?? null)
                  setMonthAnchor(null)
                }}
                style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
                className="mo-ink mo-press border-r border-line-4 px-3 py-[6px] text-[11px] font-semibold leading-none text-ink-2"
              >
                {view === 'day' ? 'Today' : view === 'month' ? 'This month' : 'This week'}
              </button>
              <button
                type="button"
                aria-label={`Next ${stepUnit}`}
                onClick={() => step(1)}
                style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
                className="mo-ink mo-press px-[10px] py-[6px] text-muted"
              >
                <ChevronRight />
              </button>
            </div>

            <div className="flex items-center gap-[2px] rounded-pill border border-line bg-surface p-[3px]">
              {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={view === mode}
                  onClick={() => setView(mode)}
                  style={
                    {
                      '--mo-rule': view === mode ? 'var(--color-surface)' : 'var(--color-ink)',
                    } as CSSProperties
                  }
                  className={[
                    'mo-ink mo-press rounded-[14px] px-[10px] py-[4px] text-[11px] capitalize leading-none',
                    view === mode ? 'bg-ink font-semibold text-surface' : 'font-medium text-muted',
                  ].join(' ')}
                >
                  {mode}
                </button>
              ))}
            </div>

            <button
              type="button"
              disabled={!focus}
              // THE ONE CENTRED PLACEMENT, and the reason is that this press names no slot. Every
              // other route into the editor starts from a point on the grid and opens beside it;
              // `+ New` means "somewhere today", so it opens where a thing with no place goes.
              onClick={() =>
                focus &&
                openComposer(
                  newDraft(focus, focus === today && nowMinutes !== null ? nowMinutes : 9 * 60),
                  null,
                )
              }
              style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
              className="mo-ink mo-press flex items-center gap-[6px] rounded-pill bg-ink px-[13px] py-[7px] text-[11px] font-semibold leading-none text-surface"
            >
              <Plus size={12} />
              New
            </button>

            <DataModeBadge />
          </div>
        </div>
      </HeaderBand>

      <div className="relative flex min-h-0 flex-1 gap-[13px] overflow-hidden px-[30px] pb-[26px]">
        {/* THE RAIL BELONGS TO BOTH WIDE VIEWS. The sources list is the same real filter in
            month view that it is in week view, and the mini-month is the one thing on the screen
            that could contradict the grid — so in MONTH view it is not an independent browser at
            all: it is anchored to `focus`, and stepping it steps the grid. In WEEK view it keeps
            the loose `monthAnchor` it always had, because there the grid shows one week and
            paging the little calendar to look ahead is the point. */}
        {(view === 'week' || view === 'month') && focus && weekStart && (
          <div className="flex w-[196px] flex-none flex-col gap-3">
            <MiniMonth
              anchor={view === 'month' ? focus : (monthAnchor ?? focus)}
              today={today}
              weekStart={weekStart}
              onPick={(date) => {
                setAnchor(date)
                setMonthAnchor(view === 'month' ? null : date)
              }}
              onStepMonth={(delta) => {
                if (view === 'month') {
                  setAnchor(shiftMonth(focus, delta))
                  setMonthAnchor(null)
                  return
                }
                setMonthAnchor(shiftMonth(monthAnchor ?? focus, delta))
              }}
            />
            <SourcesList
              hidden={hidden}
              onToggle={(key) =>
                setHidden((was) => {
                  const next = new Set(was)
                  if (!next.delete(key)) next.add(key)
                  return next
                })
              }
              googleSyncedAt={googleSyncedAt}
              outsideWindow={outsideGoogleWindow}
            />
            <ReadOnlyNote />
          </div>
        )}

        {isPending && (
          <div className="flex min-w-0 flex-1 flex-col rounded-panel border border-line-2 bg-surface p-5 shadow-calendar">
            <SkeletonRows rows={8} />
          </div>
        )}

        {/* A read that failed prints the reason. An empty grid would say "your week is free",
            which is a different and false statement. */}
        {error && (
          <div className="flex min-w-0 flex-1 items-start rounded-panel border border-line-2 bg-surface p-5 shadow-calendar">
            <p className="max-w-[620px] text-pretty text-body text-ink-3">
              {error instanceof Error ? error.message : 'The calendar could not be loaded.'}
            </p>
          </div>
        )}

        {calendar && focus && weekStart && view === 'week' && (
          <WeekView
            weekStart={weekStart}
            today={today}
            nowMinutes={nowMinutes}
            blocks={visibleBlocks}
            dueItems={visibleDue}
            allDayEvents={visibleAllDay}
            onOpenDay={(date) => {
              setAnchor(date)
              setView('day')
            }}
            drag={blockDrag.drag}
            onOpenBlock={openBlock}
            onOpenDue={openDue}
            onOpenAllDay={openAllDay}
            onCompose={(date, minutes, at) => openComposer(newDraft(date, minutes), at)}
            onDragBegin={blockDrag.begin}
          />
        )}

        {calendar && focus && view === 'day' && (
          <>
            <DayView
              date={focus}
              today={today}
              nowMinutes={nowMinutes}
              blocks={[...dayBlocks, ...suggestions]}
              dueItems={visibleDue.filter((d) => d.date === focus)}
              allDayEvents={visibleAllDay.filter((e) => e.date <= focus && focus <= e.endDate)}
              proposal={proposal}
              drag={blockDrag.drag}
              onOpenBlock={openBlock}
              onOpenDue={openDue}
              onOpenAllDay={openAllDay}
              onCompose={(minutes, at) => openComposer(newDraft(focus, minutes), at)}
              onDropAt={handleDrop}
              onDragBegin={blockDrag.begin}
              suggestionActions={(block) => (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      keepSuggestion(block)
                    }}
                    className="rounded-[14px] border border-cal-keep-line bg-surface px-[9px] py-1 text-[9.5px] font-semibold leading-none text-cal-learning-ink"
                  >
                    Keep
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDismissed((was) => new Set(was).add(block.id))
                    }}
                    className="rounded-[14px] px-[9px] py-1 text-[9.5px] font-semibold leading-none text-cal-learning-ink-2"
                  >
                    Dismiss
                  </button>
                </>
              )}
            />

            <div className="flex w-[300px] flex-none flex-col gap-[11px] overflow-y-auto">
              <UnscheduledCard
                tasks={tasks ? unscheduledTasks(tasks, dayBlocks, today ?? focus) : []}
                today={today ?? focus}
                onDragStart={setDragging}
                onDragEnd={() => {
                  setDragging(null)
                  setProposal(null)
                }}
              />
              <WhereTodayGoesCard blocks={dayBlocks} />
              <FocusCard
                deadline={nextDeadline(calendar.dueItems, today ?? focus)}
                today={today ?? focus}
                nowMinutes={nowMinutes ?? 0}
                // Anchored to the button that asked for it — the rail is 300px from the grid, and
                // a form that opened over the grid instead would make the owner look away from
                // the deadline he just pressed "block time for this" on.
                onBlockTime={(deadline, at) =>
                  openComposer(deadlineDraft(focus, nowMinutes, deadline), at)
                }
              />
              <RailFootnote />
            </div>
          </>
        )}

        {calendar && focus && view === 'month' && (
          <MonthView
            anchor={focus}
            today={today}
            blocks={visibleBlocks}
            dueItems={visibleDue}
            allDayEvents={visibleAllDay}
            onOpenDay={(date) => {
              setAnchor(date)
              setView('day')
            }}
            // The SAME handlers the week and day grids use — an editable block opens the
            // composer, a Canvas class or a Google event opens the peek, and `popover.ts` places
            // both beside whatever was clicked. No month-specific panel exists to drift.
            onOpenBlock={openBlock}
            onOpenDue={openDue}
            onOpenAllDay={openAllDay}
          />
        )}

        {/* A ROLLBACK THAT SAYS NOTHING IS A LIE WITH EXTRA STEPS. The optimistic write puts the
            block in its new place the instant the pointer is released; if the server refuses, the
            block snaps back — and without this strip that snap looks like the drag "not taking",
            which is the screen hiding a failure. The composer shows its own copy of this error,
            so the strip stands down whenever the composer is open. */}
        {!draft && update.isError && (
          <div
            role="status"
            className="absolute inset-x-0 top-4 z-[9] mx-auto flex w-[420px] flex-col gap-1 rounded-panel border border-line-2 bg-surface px-4 py-3 shadow-lifted"
          >
            <span className="text-[11.5px] font-semibold leading-none text-danger-2">
              That move was not saved
            </span>
            <span className="text-pretty text-[10.5px] font-normal leading-[1.45] text-muted">
              {update.error instanceof Error
                ? update.error.message
                : 'The block could not be updated.'}{' '}
              The block is back where it was.
            </span>
          </div>
        )}

        {overlay?.kind === 'composer' && (
          <Composer
            draft={overlay.draft}
            anchor={overlay.anchor}
            onSave={save}
            onDelete={(id) => remove.mutate(id, { onSuccess: closeOverlay })}
            onCancel={closeOverlay}
            pending={create.isPending || update.isPending}
            error={
              create.error instanceof Error
                ? create.error.message
                : update.error instanceof Error
                  ? update.error.message
                  : undefined
            }
          />
        )}

        {overlay?.kind === 'peek' && (
          <CalendarPeek anchor={overlay.anchor} content={overlay.content} onClose={closeOverlay} />
        )}
      </div>
    </>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A local wall clock, as an ISO instant that still SAYS the wall clock.
 *
 * `toISOString()` would be correct and unreadable — it hands the demo implementation a UTC hour
 * to slice, which is how a 3 PM block ends up drawn at 10 PM with nobody at fault. The offset is
 * the browser's own, which is the owner's own, which is the only zone this single-user app has.
 */
function localTimestamp(date: ISODate, minutes: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const at = new Date(y!, m! - 1, d!, Math.floor(minutes / 60), minutes % 60, 0, 0)
  const offset = -at.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0')
  return `${date}T${pad(minutes / 60)}:${pad(minutes % 60)}:00${sign}${pad(offset / 60)}:${pad(offset % 60)}`
}

function newDraft(date: ISODate, minutes: number): ComposerDraft {
  const start = snapToQuarter(minutes)
  return {
    date,
    startMinutes: start,
    endMinutes: start + 60,
    title: '',
    source: 'personal',
    location: null,
    taskId: null,
    // NONE, and not a default of ten minutes. Picking an offset for him would be the app
    // inventing a number; the picker is right there if he wants one.
    reminders: [],
  }
}

function draftOf(block: CalendarBlock): ComposerDraft {
  return {
    id: block.id,
    date: block.date,
    startMinutes: block.startMinutes,
    endMinutes: block.endMinutes,
    title: block.title,
    // NEITHER READ-ONLY SOURCE EVER REACHES HERE: a Canvas class and a Google event DO have a
    // click now, but `openBlock` routes it to the detail peek before this function is ever
    // called (`if (!block.editable) { ...; return }`) — the composer opens only for a block
    // LifeOS itself owns. The fallback exists so the composer's source stays inside the
    // WRITABLE four regardless — the type refuses the other two, which is brief rule 1 enforced
    // by the compiler rather than by this line.
    source: block.source === 'canvas' || block.source === 'google' ? 'personal' : block.source,
    location: block.location,
    taskId: block.taskId,
    // `?? []` because the field is optional in the shared type: every fixture and view written
    // before notifications existed still says "none" by saying nothing.
    reminders: block.reminders ?? [],
  }
}

// ── The detail peek's content, built from whichever read-only entity was clicked ───────────

/** "From Google · synced 6 min ago" when the connector's own last sync is known; the bare
 *  statement otherwise — never a relative time this screen was not actually told. */
function googleProvenance(
  googleSyncedAt: string | null | undefined,
  today: ISODate | undefined,
  nowMinutes: number | null,
): string {
  return googleSyncedAt && today && nowMinutes !== null
    ? `From Google · synced ${agoPhrase(googleSyncedAt, today, nowMinutes)}`
    : 'From Google'
}

/** A Canvas class or a timed Google event, from the grid. */
function peekFromBlock(
  block: CalendarBlock,
  googleSyncedAt: string | null | undefined,
  today: ISODate | undefined,
  nowMinutes: number | null,
): PeekContent {
  return {
    title: block.title,
    timeRange: formatTimeRange(block.startMinutes, block.endMinutes),
    location: block.location,
    // Rendered only when Google said something other than the silent default.
    status: block.status && block.status !== 'confirmed' ? block.status : null,
    provenance: block.proposalId
      ? 'From chat'
      : block.source === 'google'
        ? googleProvenance(googleSyncedAt, today, nowMinutes)
        : 'From Canvas',
    courseSlug: block.courseSlug,
  }
}

/** A Canvas due item, from the all-day row (week) or the header pills (day). */
function peekFromDue(item: CalendarDueItem): PeekContent {
  return {
    title: item.title,
    // The same literal-or-honest rule the due row itself keeps (brief rule 5): a real time from
    // Canvas, or the words "no time given" — never a manufactured 11:59 PM.
    timeRange: item.hasTime && item.dueMinutes !== null ? `Due ${compactTime(item.dueMinutes)}` : 'Due · no time given',
    location: null,
    status: null,
    provenance: 'From Canvas',
    courseSlug: item.courseSlug,
  }
}

/** A whole-day Google event, from the all-day row (week) or the header pills (day). */
function peekFromAllDay(
  event: CalendarAllDayEvent,
  googleSyncedAt: string | null | undefined,
  today: ISODate | undefined,
  nowMinutes: number | null,
): PeekContent {
  return {
    title: event.title,
    // "All day" is Google's own fact, not a time this screen picked (see WeekView/DayView).
    timeRange: 'All day',
    location: event.location,
    status: event.status !== 'confirmed' ? event.status : null,
    provenance: googleProvenance(googleSyncedAt, today, nowMinutes),
    // A whole-day event never came from a course — there is nowhere for it to link.
    courseSlug: null,
  }
}

/**
 * "Block time for this" on the focus card. It opens the composer PREFILLED — it does not create
 * anything. Naming a deadline is not the same as choosing when to work on it, and the app does
 * not make that choice on the owner's behalf.
 */
function deadlineDraft(
  date: ISODate,
  nowMinutes: number | null,
  deadline: CalendarDueItem,
): ComposerDraft {
  const start = snapToQuarter(nowMinutes ?? CALENDAR_DAY_START_HOUR * 60)
  return {
    date,
    startMinutes: start,
    endMinutes: start + 60,
    title: deadline.title,
    source: 'task',
    location: null,
    taskId: null,
    // Naming a deadline is not the same as asking to be told about it, either.
    reminders: [],
  }
}

/**
 * The same day-of-month in another month — except that it always lands on the 1st.
 *
 * THE DAY IS RESET FIRST, and that is the whole reason this is a function. `setUTCMonth(m - 1)`
 * on the 31st of a month walks into the following month (Jan 31 → Mar 3), which is how a "step
 * back one month" button silently skips February. Setting the day in the SAME call — the second
 * argument — is what makes every step land where the label says it will.
 */
function shiftMonth(date: ISODate, delta: number): ISODate {
  const d = toDate(date)
  d.setUTCMonth(d.getUTCMonth() + delta, 1)
  return d.toISOString().slice(0, 10) as ISODate
}

/** 'Aug 24 – 30, 2026' — the week label in the header's meta row. */
function rangeLabel(weekStart: ISODate): string {
  const end = addDays(weekStart, 6)
  const sameMonth = weekStart.slice(0, 7) === end.slice(0, 7)
  return sameMonth
    ? `${monthName(weekStart, true)} ${toDate(weekStart).getUTCDate()} – ${toDate(end).getUTCDate()}, ${end.slice(0, 4)}`
    : `${formatShortDate(weekStart)} – ${formatShortDate(end)}, ${end.slice(0, 4)}`
}
